/* =====================================================================
 * egt.trackrow.js — the SHARED per-track ROW builder.
 *
 * Extracted from app/elements/egt.album.js so the SAME row (title + Exploit
 * "Character:" POV + one-line feeling + the fs.existsSync-gated preview player +
 * the --solo no-subtext modifier) can be rendered on BOTH the per-album detail
 * pages (egt.album.js) and the /exploit soundtrack tabs (egt.exploit.js). One
 * source, so the two surfaces can never drift.
 *
 * trackRow(albumId, t, i):
 *   albumId — the ALBUMS id (used to resolve the preview file on disk).
 *   t       — a plain title string (music albums) OR an object
 *             { title, pov, feeling } (Exploit albums; instrumentals carry a
 *             feeling but no pov).
 *   i       — the ZERO-BASED index (track no = i + 1; also the --egt-i stagger).
 * Returns the UA/JSON <li> row. Pure builder, no behaviour — the client player
 * lives in app/public/src/egt.album.js.
 * ===================================================================== */
var fs = require("fs");
var path = require("path");

/* AUDIO PREVIEW CONVENTION (scaffolded, honest): a track's preview lives at
 * app/public/audio/albums/<album-id>/<NN>.m4a (NN = zero-padded 1-based track
 * number), served at /audio/albums/<album-id>/<NN>.m4a. At render time we
 * fs.existsSync-check the file: if present we render a play/pause button with a
 * radial progress ring (client behaviour in /src/egt.album.js); if absent we
 * render NO control at all — the row is just the listing. Graceful, never a
 * broken player. */
/* SERVER-ONLY path. This helper is also bundled into the BROWSER (egt.album is in
 * the client registry), where webpack stubs `path`/`fs` with empty objects. Calling
 * path.join at load time there threw, which made the whole app client registry fall
 * back to UAT defaults ("App registry (client) not found") and broke every
 * client-only control (e.g. the /forge/start fields) from 2026-07-31 to 2026-10-09.
 * So: compute lazily, and only when Node's path/fs are really there. */
var CAN_FS = !!(path && typeof path.join === "function" && fs && typeof fs.existsSync === "function");
var AUDIO_DIR_ABS = CAN_FS ? path.join(__dirname, "..", "public", "audio", "albums") : null;
function pad2(n) { return (n < 10 ? "0" : "") + n; }
function previewFor(albumId, trackNo) {
  if (!CAN_FS) { return null; }
  var nn = pad2(trackNo);
  var abs = path.join(AUDIO_DIR_ABS, albumId, nn + ".m4a");
  var exists = false;
  try { exists = fs.existsSync(abs); } catch (e) { exists = false; }
  return exists ? ("/audio/albums/" + albumId + "/" + nn + ".m4a") : null;
}

/* Small inline glyphs for the scaffolded player (kept tiny; only render when a
 * preview file actually exists). */
function playIcon() {
  return { n: "svg", c: "egt-album-play__glyph egt-album-play__glyph--play", viewBox: "0 0 24 24",
    "aria-hidden": "true", focusable: "false",
    i: { n: "path", fill: "currentColor", d: "M8 5v14l11-7z" } };
}
function pauseIcon() {
  return { n: "svg", c: "egt-album-play__glyph egt-album-play__glyph--pause", viewBox: "0 0 24 24",
    "aria-hidden": "true", focusable: "false",
    i: [ { n: "rect", x: "6", y: "5", width: "4", height: "14", rx: "1", fill: "currentColor" },
      { n: "rect", x: "14", y: "5", width: "4", height: "14", rx: "1", fill: "currentColor" } ] };
}
/* The radial progress RING: two concentric SVG circles (a faint track + the gold
 * fill). The fill's stroke-dashoffset is animated from playback in the client;
 * the geometry (r=16 → circumference ≈ 100.53) is mirrored in egt.album.scss and
 * /src/egt.album.js. */
function playerButton(src, title) {
  return {
    n: "button", type: "button", c: "egt-album-play",
    "data-egt-audio": src, "aria-pressed": "false",
    "aria-label": "Play a preview of " + title,
    i: [
      { n: "svg", c: "egt-album-play__ring", viewBox: "0 0 36 36", "aria-hidden": "true", focusable: "false",
        i: [
          { n: "circle", c: "egt-album-play__ring-track", cx: "18", cy: "18", r: "16", fill: "none" },
          { n: "circle", c: "egt-album-play__ring-fill", cx: "18", cy: "18", r: "16", fill: "none" }
        ] },
      { n: "span", c: "egt-album-play__glyphs", "aria-hidden": "true", i: [ playIcon(), pauseIcon() ] }
    ]
  };
}

/* The RIAA/Apple-style "E" explicit mark — a small muted square glyph. Rendered
 * before the title on records/tracks that carry an explicit flag (album-level via
 * ALBUMS `explicit: true`, or per-track via `t.explicit`). role=img + aria-label
 * so assistive tech announces "Explicit" (the bare "E" glyph is decorative). */
function explicitMark() {
  return { n: "span", c: "egt-album-trk__ex", role: "img",
    "aria-label": "Explicit", title: "Explicit", i: "E" };
}

/* One tracklist row. `t` is either a plain title string (music albums) or an
 * object { title, pov, feeling } (Exploit albums). Instrumentals carry a feeling
 * but no pov. The 30s PREVIEW player renders whenever the preview clip exists on
 * disk (fs.existsSync), REGARDLESS of released vs prerelease — a prerelease TEASES
 * its previews (founder decision). Release state gates streaming links,
 * "Releases on" and the presave CTA at the CALL SITE, never the preview player.
 * `albumExplicit` (4th arg) marks EVERY row on an explicit record; a per-track
 * `t.explicit` can also set it individually. */
function trackRow(albumId, t, i, albumExplicit) {
  var no = i + 1;
  var isObj = t && typeof t === "object";
  var title = isObj ? t.title : t;
  var pov = isObj ? t.pov : null;
  var feeling = isObj ? t.feeling : null;
  var explicit = albumExplicit || (isObj && t.explicit) || false;

  var preview = previewFor(albumId, no);

  var lead = [];
  if (preview) { lead.push(playerButton(preview, title)); }
  lead.push({ n: "span", c: "egt-album-trk__no", "aria-hidden": "true", i: pad2(no) });

  var titleRow = [ { n: "span", c: "egt-album-trk__title", i: title } ];
  if (explicit) { titleRow.push(explicitMark()); }
  var body = [ { n: "span", c: "egt-album-trk__titlerow", i: titleRow } ];
  if (pov) {
    body.push({ n: "span", c: "egt-album-trk__pov",
      i: [ { n: "span", c: "egt-album-trk__pov-k", i: "Character:" }, pov ] });
  }
  if (feeling) { body.push({ n: "span", c: "egt-album-trk__feeling", i: feeling }); }

  /* A row with NO subtext (no character POV and no feeling — the plain-title
   * music tracks) has only the title in its body. Center it vertically in the
   * row (egt-album-trk--solo) instead of top-aligning it and leaving the empty
   * space where the subtext stack would otherwise sit. Rows WITH subtext (every
   * Exploit track, incl. instrumentals which carry a feeling) keep the top-
   * aligned title + Character: + feeling stack. */
  var solo = !pov && !feeling;

  return {
    n: "li",
    c: "egt-album-trk egt-reveal"
      + (solo ? " egt-album-trk--solo" : "")
      + (preview ? " egt-album-trk--has-audio" : ""),
    style: "--egt-i:" + i,
    i: [
      { n: "div", c: "egt-album-trk__lead", i: lead },
      { n: "div", c: "egt-album-trk__body", i: body }
    ]
  };
}

exports.trackRow = trackRow;
exports.previewFor = previewFor;
